/**
 * Notifications / needs-attention model.
 *
 * The QA-flagged gap: approvals pile up unseen, at-risk accounts slip, and
 * overdue follow-ups scroll away. `deriveNotifications` is a PURE derivation
 * that turns already-loaded state — pending approvals, customer-health
 * results, follow-up reminders, and system-scan findings — into a single
 * prioritized list a badge / inbox widget can render directly.
 *
 * Deliberately no store access and no writes: callers load state through the
 * existing adapters (`listApprovals`, `rankCustomersByRisk`,
 * `getFollowUpReminders`, `scanSystem`) and hand the snapshots in, so this can
 * run inside a `useMemo` and never drifts out of sync with the source data.
 *
 * Ranking is simple and explainable, mirroring the scanner's philosophy:
 * severity bucket first (high → medium → low), then kind (approvals first —
 * they gate real work), then an urgency magnitude within the bucket (more
 * overdue / lower health / bigger finding first).
 */

import type { ApprovalItem } from '../db/types';
import type { Person } from '../db/types';
import type { CustomerHealth } from './health';
import type { FollowUpReminder } from './reminders';
import type { ScanFinding } from '../agent/systemScanner';
import { reminderLabel } from './reminders';

export type NotificationKind = 'approval' | 'mention' | 'at-risk' | 'overdue' | 'finding';

export type NotificationSeverity = 'high' | 'medium' | 'low';

export interface Notification {
  /** Stable, derivation-unique id (kind + source-entity id). */
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  /** One-line headline, e.g. "Approval pending · Add customer". */
  title: string;
  /** Supporting detail line. */
  detail: string;
  /** Id of the underlying record, for deep-linking (approval / person / entity id). */
  target: string;
}

/** One at-risk-health entry: a person paired with their computed health. */
export interface HealthEntry {
  person: Person;
  health: CustomerHealth;
}

/**
 * A precomputed @mention notice. The caller (NotificationsBell) resolves
 * which comment mentions the current user from real project/task comment
 * data — nothing here is inferred, so a mention only appears when the
 * viewer's name genuinely appears in a comment's `mentions[]`.
 */
export interface MentionNotice {
  /** Stable id, e.g. `${projectId}:${commentId}`. */
  id: string;
  authorName: string;
  /** Where it happened, e.g. "Project · Billing Automation". */
  context: string;
  /** Short body excerpt. */
  excerpt: string;
  /** Deep-link target (project or task id). */
  target: string;
  /** ISO timestamp of the comment (newer mentions surface first). */
  createdAt: string;
}

export interface NotificationsInput {
  /** Approvals snapshot — only `status === 'pending'` items surface. */
  approvals?: ApprovalItem[];
  /** Customer health results (e.g. from `rankCustomersByRisk`) — only the 'at-risk' band surfaces. */
  health?: HealthEntry[];
  /** Follow-up reminders (e.g. from `getFollowUpReminders`) — only `urgency === 'overdue'` surfaces. */
  reminders?: FollowUpReminder[];
  /** Scan findings (e.g. from `scanSystem().findings`) — only `severity === 'high'` surfaces. */
  findings?: ScanFinding[];
  /** @mentions of the current user, precomputed from project/task comments. */
  mentions?: MentionNotice[];
}

export interface NotificationCounts {
  total: number;
  byKind: Record<NotificationKind, number>;
}

const SEVERITY_RANK: Record<NotificationSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** Within a severity bucket: approvals gate work, then personal @mentions. */
const KIND_RANK: Record<NotificationKind, number> = {
  approval: 0,
  mention: 1,
  'at-risk': 2,
  overdue: 3,
  finding: 4,
};

/** Internal ranked wrapper — `urgency` orders items inside a (severity, kind) bucket. */
interface Ranked {
  notification: Notification;
  urgency: number;
}

/**
 * Derive the prioritized needs-attention list. Pure: no I/O, inputs are not
 * mutated, and empty/omitted inputs honestly yield an empty list.
 */
export function deriveNotifications(input: NotificationsInput): Notification[] {
  const ranked: Ranked[] = [];

  // --- Pending approvals: the headline problem — sign-offs piling up unseen.
  for (const a of input.approvals ?? []) {
    if (a.status !== 'pending') continue;
    // Use the render-ready summary stamped at queue time; older records
    // without one fall back to the action kind rather than re-deriving via
    // the executor layer (which would drag store adapters into a pure module).
    const sum = a.summary ?? { verb: a.action.kind.replace(/_/g, ' '), detail: '' };
    const ageMs = Date.parse(a.createdAt);
    ranked.push({
      notification: {
        id: `approval:${a.id}`,
        kind: 'approval',
        severity: 'high',
        title: `Approval pending · ${sum.verb}`,
        detail: [sum.detail, `from ${a.source}`].filter(Boolean).join(' · '),
        target: a.id,
      },
      // Older proposals have waited longest — surface them first.
      urgency: Number.isNaN(ageMs) ? 0 : -ageMs,
    });
  }

  // --- At-risk customers: only the at-risk band; lower score = more urgent.
  for (const entry of input.health ?? []) {
    if (entry.health.band !== 'at-risk') continue;
    const top = entry.health.factors
      .filter((f) => f.points < 0)
      .map((f) => f.label)
      .slice(0, 2)
      .join(' · ');
    ranked.push({
      notification: {
        id: `at-risk:${entry.person.id}`,
        kind: 'at-risk',
        severity: 'high',
        title: `At-risk customer · ${entry.person.name}`,
        detail: `Health ${entry.health.score}/100${top ? ` · ${top}` : ''}`,
        target: entry.person.id,
      },
      urgency: 100 - entry.health.score,
    });
  }

  // --- Overdue follow-ups: only the truly overdue subset; older = higher.
  for (const r of input.reminders ?? []) {
    if (r.urgency !== 'overdue') continue;
    ranked.push({
      notification: {
        id: `overdue:${r.person.id}`,
        kind: 'overdue',
        severity: 'medium',
        title: `Follow-up overdue · ${r.person.name}`,
        detail: [reminderLabel(r), r.nextStep].filter(Boolean).join(' · '),
        target: r.person.id,
      },
      urgency: -r.daysUntil,
    });
  }

  // --- @mentions of the viewer: precomputed from real comments; newest first.
  for (const m of input.mentions ?? []) {
    const ts = Date.parse(m.createdAt);
    ranked.push({
      notification: {
        id: `mention:${m.id}`,
        kind: 'mention',
        severity: 'medium',
        title: `Mention · ${m.authorName}`,
        detail: [m.context, m.excerpt].filter(Boolean).join(' · '),
        target: m.target,
      },
      // Newer mentions surface first (sort is descending on urgency).
      urgency: Number.isNaN(ts) ? 0 : ts,
    });
  }

  // --- High scan findings: pass through the scanner's own ordering key.
  for (const f of input.findings ?? []) {
    if (f.severity !== 'high') continue;
    ranked.push({
      notification: {
        id: `finding:${f.id}`,
        kind: 'finding',
        severity: 'high',
        title: `${f.category} · ${f.title}`,
        detail: f.detail,
        target: f.entityId,
      },
      urgency: f.sortKey,
    });
  }

  ranked.sort((a, b) => {
    const sev =
      SEVERITY_RANK[a.notification.severity] - SEVERITY_RANK[b.notification.severity];
    if (sev !== 0) return sev;
    const kind = KIND_RANK[a.notification.kind] - KIND_RANK[b.notification.kind];
    if (kind !== 0) return kind;
    if (a.urgency !== b.urgency) return b.urgency - a.urgency;
    return a.notification.title.localeCompare(b.notification.title);
  });

  return ranked.map((r) => r.notification);
}

/**
 * Badge-ready counts over the same input: total notifications plus a
 * per-kind breakdown (zeros when nothing needs attention).
 */
export function notificationCount(input: NotificationsInput): NotificationCounts {
  const notifications = deriveNotifications(input);
  const byKind: Record<NotificationKind, number> = {
    approval: 0,
    mention: 0,
    'at-risk': 0,
    overdue: 0,
    finding: 0,
  };
  for (const n of notifications) byKind[n.kind] += 1;
  return { total: notifications.length, byKind };
}
